/*
  The breadcrumb trail (D25), off MUI and off Emotion.

  Unlike the AppBar and the Footer this is NOT chrome — it sits on the page, inside the
  shell's <main>, so it themes like any other card (D14 names three fixed surfaces and
  this is not one of them).

  Its links were `#cf974f`, the third gold D25 retires. That colour maps to the gold TEXT
  token, so they are the shared LINK from components/styles: `text-pcg-goldText`, #D8A24A
  in dark and #865F1A on paper. Never `text-pcg-gold` — the fill token reads 1.69:1 as
  light-mode text, which fails AA outright (D20). LINK also brings the
  feature's focus ring, so the trail rings the same way every other link in the app does.

  MUI's <Breadcrumbs> gave the nav landmark, the "/" separators and the list semantics.
  All three are written out here: `<nav aria-label="breadcrumb">` (unchanged from what it
  rendered), an ordered list, separators marked aria-hidden so a screen reader is not read
  a slash between every crumb, and the crumb with no url — the page you are on — marked
  `aria-current="page"`, which MUI never did.

  The panel keeps its shape: 1rem radius, 1rem/2rem padding, and a wash of brand gold
  across it. The wash used to be a hard `#fff → #debb68` gradient, which would have been a
  white bar on the dark page; it is the card surface with the gold at 10% over it now, so
  the flavour survives the theme. Measured: goldText on that wash is 7.1:1 in dark and
  5.6:1 in light.

  One deliberate change: the current crumb was a `variant='h6'` Typography, 20px against
  16px links for no reason anyone recorded. The trail is one size now, with the current
  crumb carrying weight instead. It was never a real heading element and still is not.

  **On the inner pages the trail lives in the header, not on the page** (D15 of
  docs/specs/pop-language-split.md, Robert, 2026-10-20: "Breadcrumb replaces it"). The
  views still render <Breadcrumbs> first thing in their body, because that is where the
  crumbs' data is; this component then portals itself into the slot SearchCardsRow leaves
  under the "Cert Lookup & Pop Search" heading, where the event skin's gold rule was. The
  slot is looked up in a layout effect, and nothing renders until it has been: the
  setState there re-renders before the browser paints, so the trail never flashes on the
  page first and then jumps up. With no slot (home has none, and neither does a test that
  renders a view on its own) the trail renders inline, panel and all, as it always did.

  In the slot it is compact: no panel, no `skin-crumbs`, small text. The panel is a page
  device — inside the header it would be a box within a band — and leaving the marker
  class off is what keeps gilded's hairline and padding out of it as well as navy's wash.
*/

import { useLayoutEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";

import { LINK } from "components/styles";

interface Crumb {
  url: string | null;
  text: string;
}

interface BreadcrumbsProps {
  crumbs: Crumb[];
}

/* The header slot SearchCardsRow renders on every page but home (D15). */
export const CRUMBS_SLOT_ID = "pcg-crumbs-slot";

const PANEL_LIST =
  "skin-crumbs flex flex-wrap items-center gap-x-2 gap-y-1 rounded-2xl " +
  "border border-pcg-border bg-pcg-card bg-gradient-to-r from-transparent " +
  "to-pcg-gold/10 px-8 py-4 text-pcg-text";

const COMPACT_LIST = "flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-pcg-text";

const Breadcrumbs = (props: BreadcrumbsProps) => {
  const { crumbs } = props;

  /* undefined until looked up; null once looked up and absent. */
  const [slot, setSlot] = useState<HTMLElement | null | undefined>(undefined);

  useLayoutEffect(() => {
    setSlot(document.getElementById(CRUMBS_SLOT_ID));
  }, []);

  if (slot === undefined) {
    return null;
  }

  const trail = (
    <nav aria-label="breadcrumb" className={slot ? undefined : "pb-4"}>
      <ol className={slot ? COMPACT_LIST : PANEL_LIST}>
        {crumbs.map((crumb: Crumb, index: number) => {
          const { url, text } = crumb;
          return (
            <li key={`${index}-${text}`} className="flex items-center gap-x-2">
              {index > 0 ? (
                <span aria-hidden="true" className="text-pcg-textMuted">
                  /
                </span>
              ) : null}
              {url ? (
                <Link to={url} className={LINK}>
                  {text}
                </Link>
              ) : (
                <span aria-current="page" className="font-semibold">
                  {text}
                </span>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );

  return slot ? createPortal(trail, slot) : trail;
};

export default Breadcrumbs;
